export interface ResizeCatalogImageOptions {
  /** Target width / height ratio (default 4:3 to match the customer detail page). */
  aspect?: number;
  /** Never upscale past this width (default 3840 ≈ 4K). */
  maxWidth?: number;
  /** JPEG quality 0-1. */
  quality?: number;
}

/**
 * Center-crops an uploaded image to `aspect`, downscales it so the width does
 * not exceed `maxWidth` (never upscales — a smaller photo keeps its native
 * resolution), and returns a JPEG File ready to upload. Falls back to the
 * original file if the browser cannot decode it.
 */
export async function resizeCatalogImage(
  file: File,
  options: ResizeCatalogImageOptions = {},
): Promise<File> {
  const aspect = options.aspect ?? 4 / 3;
  const maxWidth = options.maxWidth ?? 3840;
  const quality = options.quality ?? 0.9;
  try {
    const source = await loadImage(file);
    const width = source.width;
    const height = source.height;
    if (!width || !height) return file;

    // Center-crop (cover) to the target aspect.
    let cropW = width;
    let cropH = Math.round(width / aspect);
    if (cropH > height) {
      cropH = height;
      cropW = Math.round(height * aspect);
    }
    const sx = Math.round((width - cropW) / 2);
    const sy = Math.round((height - cropH) / 2);

    // Downscale only; never upscale.
    const outW = Math.min(cropW, maxWidth);
    const outH = Math.round(outW / aspect);

    const canvas = document.createElement("canvas");
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(source, sx, sy, cropW, cropH, 0, 0, outW, outH);
    if ("close" in source) source.close();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", quality),
    );
    if (!blob) return file;

    const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], name, { type: "image/jpeg" });
  } catch {
    return file;
  }
}

async function loadImage(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      // fall through to <img>
    }
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Could not load image"));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}
